import { TOLERANSI_HARGA_TERIMA, uangKasir } from "./rincianBayar";

/* ============================================================================
 * Satu sumber pemetaan BARIS BARANG & DISKON nota untuk dua layar kasir:
 * Input Nota (`pages/InputNota.jsx`, draf `nota_sales`) dan Input Penjualan
 * (`pages/Sales.jsx`, dokumen `sales`). Batch N2 (brief owner 22 Sep 2026,
 * D2 + D3): diskon pindah dari tingkat NOTA ke tiap baris barang.
 *
 * KENAPA ADA
 * - Kedua layar dulu memetakan baris barang dokumen -> form dengan DAFTAR-PUTIH
 *   medan yang diketik ulang di masing-masing berkas. Medan baru yang lupa
 *   ditambahkan ke daftar itu TERBUANG begitu dokumen dibuka, lalu ikut hilang
 *   saat disimpan — untuk nota bertempo, diskon yang hilang berarti piutang
 *   naik tanpa satu galat pun (tinjauan N2/N3, keberatan
 *   `tes-openedit-membuang-diskon-dan-keterangan`). Pemetaannya kini di SINI,
 *   dan diuji pulang-pergi terhadap respons GET sungguhan
 *   (`backend/tests/test_n2_payload_js.py`).
 * - Rumus diskon di layar harus sama dengan server (`routers/sales.py`:
 *   `diskon_baris`, `diskon_baris_total`, `harga_bersih_baris`,
 *   `diskon_terposting`). Hanya mengimpor lib murni lain (`./rincianBayar`),
 *   jadi tetap bisa dijalankan node lewat harness Babel (`tests/_n2_node.py`).
 *
 * Batch N3 memperluas berkas ini dengan baris PEMBAYARAN (bagian bawah berkas).
 * ========================================================================== */

/** Penanda klien yang dikirim di payload (`skema_nota`). 2 = layar tahu
 *  `items[].diskon_rp` dan tidak lagi punya kolom diskon nota/biaya admin.
 *  Server memakainya untuk menolak klien lama yang akan membuang diskon baris
 *  (409 "muat ulang") dan menolak diskon nota BARU dari layar ini. */
export const SKEMA_NOTA = 2;

const bulat2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

/** Diskon (Rp) satu baris. Baris sintetik komponen paket tidak pernah
 *  berdiskon — pendapatan & potongan paket ada di baris jangkarnya. */
export function diskonBaris(it) {
  if (!it || it.is_bundle_component) return 0;
  const d = Number(it.diskon_rp);
  return Number.isFinite(d) && d > 0 ? d : 0;
}

export function diskonBarisTotal(items) {
  return bulat2((items || []).reduce((a, it) => a + diskonBaris(it), 0));
}

/** Harga baris SETELAH diskonnya — dasar peringatan "di bawah modal". */
export function hargaBersihBaris(it) {
  return (Number(it?.harga) || 0) - diskonBaris(it);
}

/** Kunci yang dibaca posting & plafon retur (`diskon_nominal`). */
export function diskonTerposting(sale) {
  return Number(sale?.diskon_nominal) || 0;
}

/** Diskon NOTA & biaya admin lama (sebelum N2) yang masih menempel di dokumen
 *  yang dibuka. `draf=true` untuk `nota_sales` (kunci `diskon`), selain itu
 *  dokumen `sales` (kunci `diskon_nominal`). Untuk dokumen yang barisnya
 *  berdiskon, `diskon_nominal` adalah TURUNAN jumlah baris — bukan diskon nota,
 *  jadi dianggap 0 (cermin `update_sale`). */
export function ambilDiskonLama(dok, { draf = false } = {}) {
  const barisAda = diskonBarisTotal(dok?.items) > 0;
  const diskon = barisAda ? 0 : (Number(draf ? dok?.diskon : dok?.diskon_nominal) || 0);
  const mode = dok?.biaya_admin_mode || "";
  const nilai = Number(dok?.biaya_admin_nilai) || 0;
  return {
    diskon,
    pic: (dok?.diskon_pic_approval || "").trim(),
    mode, nilai,
    adminRp: draf ? null : (Number(dok?.biaya_admin_rp) || 0),
    ada: diskon > 0 || (!!mode && nilai > 0),
    dihapus: false,
  };
}

/** Diskon nota yang BERLAKU untuk pratinjau layar: jumlah diskon baris, atau
 *  diskon nota lama yang masih dipertahankan. Tidak pernah keduanya — server
 *  menolak campuran (`diskon_total_nota`). */
export function diskonEfektifForm(form) {
  const baris = diskonBarisTotal(form?.items);
  if (baris > 0) return baris;
  const lama = form?._diskon_lama;
  return lama && !lama.dihapus ? lama.diskon : 0;
}

/** Biaya admin lama yang masih berlaku ({mode, nilai}); form baru tidak punya
 *  kolomnya, jadi selain dokumen lama hasilnya selalu tanpa biaya admin. */
export function adminEfektifForm(form) {
  const lama = form?._diskon_lama;
  if (!lama || lama.dihapus || !lama.mode) return { mode: "", nilai: 0 };
  return { mode: lama.mode, nilai: lama.nilai };
}

/** Pesan yang SAMA dengan 400 backend saat diskon baris bertemu diskon nota
 *  lama yang belum dihapus (aturan #3: jangan biarkan tombol berujung 400). */
export function galatCampurDiskon(form) {
  const lama = form?._diskon_lama;
  if (diskonBarisTotal(form?.items) > 0 && lama && !lama.dihapus && lama.diskon > 0) {
    return "Nota ini masih memakai diskon nota lama — hapus dulu diskon nota lama "
      + "(tombol \"Hapus diskon lama\") sebelum memberi diskon per barang.";
  }
  return null;
}

/** Pesan yang SAMA dengan 400 backend untuk isi kolom diskon satu baris. */
export function galatDiskonBaris(items) {
  for (const it of items || []) {
    const d = diskonBaris(it);
    if (d <= 0) continue;
    const nama = (it.nama_barang || "").trim() || "barang";
    if (it.is_bonus) return `Barang bonus "${nama}" tidak bisa diberi diskon — harganya sudah Rp 0.`;
    if (d > (Number(it.harga) || 0) + 0.01) return `Diskon pada "${nama}" melebihi harganya.`;
  }
  return null;
}

/** Baris barang dokumen (`sales.items[]` / `nota_sales.items[]`) -> baris form.
 *  `tebakKategori(nama)` = cadangan kategori untuk dokumen dari sebelum medan
 *  kategori ada (dioper pemanggil supaya berkas ini tetap tanpa impor). */
export function itemFormDariDokumen(it, tebakKategori) {
  const x = it || {};
  return {
    nama_barang: x.nama_barang || "",
    harga: x.harga || 0,
    kode: x.kode || "HP",
    kategori: x.kategori || (tebakKategori ? tebakKategori(x.nama_barang || "") : ""),
    // SKU & IMEI yang tersimpan WAJIB ikut termuat — kalau tidak, menyimpan
    // ulang dokumen lama menghapus tautan katalog & unit fisiknya.
    sku: x.sku || "",
    imei: x.imei || "",
    is_bonus: !!x.is_bonus,
    // Snapshot komponen paket dinormalkan ke kontrak `KomponenBundleItem`
    // (sku/qty/imei); medan tampilan snapshot tidak dibaca backend.
    komponen: Array.isArray(x.komponen) && x.komponen.length
      ? x.komponen.map(k => ({ sku: k.sku, qty: k.qty, imei: k.imei || null }))
      : null,
    // N2: diskon baris. Dokumen lama tanpa medan ini -> 0.
    diskon_rp: Number(x.diskon_rp) || 0,
  };
}

/** Daftar baris dokumen -> baris form, TANPA baris sintetik komponen paket
 *  (dibangun backend; bukan baris yang boleh disunting). */
export function itemsFormDariDokumen(items, tebakKategori) {
  return (items || []).filter(it => !it?.is_bundle_component)
    .map(it => itemFormDariDokumen(it, tebakKategori));
}

/** Baris form -> baris payload. `diskon_rp` SELALU dikirim eksplisit (0 pun):
 *  server menolak klien `skema_nota` 2 yang tidak membawanya di setiap baris,
 *  supaya baris yang kehilangan medannya tidak diam-diam jadi tanpa diskon. */
export function itemKePayload(it) {
  const x = it || {};
  return { ...x, diskon_rp: x.is_bonus ? 0 : bulat2(diskonBaris(x)) };
}

export function itemsKePayload(items) {
  return (items || []).map(itemKePayload);
}

/** Bagian DISKON payload (dipakai kedua layar).
 *
 *  `jalur` "nota" (draf, kunci `diskon`) atau "sales" (kunci `diskon_nominal`).
 *  - `skema_nota` selalu dikirim.
 *  - Diskon nota/biaya admin LAMA TIDAK dikirim (server mempertahankan nilai
 *    tersimpan), kecuali kasir menekan "Hapus diskon lama" -> dikirim 0/null.
 *  - PIC: dikirim bila ada diskon baris; `null` bila tidak ada diskon apa pun;
 *    TIDAK dikirim selama diskon nota lama dipertahankan (PIC-nya milik diskon
 *    itu dan ikut dipertahankan server — keberatan
 *    `uang-pic-diskon-lama-hilang-patch-draf`). */
export function bagianDiskonPayload(form, jalur = "nota") {
  const out = { skema_nota: SKEMA_NOTA };
  const baris = diskonBarisTotal(form?.items);
  const lama = form?._diskon_lama;
  const pic = (form?.diskon_pic_approval || "").trim() || null;
  const kunciDiskon = jalur === "sales" ? "diskon_nominal" : "diskon";
  if (lama && lama.dihapus) {
    out[kunciDiskon] = 0;
    out.biaya_admin_mode = null;
    out.biaya_admin_nilai = 0;
    out.diskon_pic_approval = baris > 0 ? pic : null;
    return out;
  }
  if (baris > 0) {
    out.diskon_pic_approval = pic;
  } else if (!(lama && lama.ada)) {
    out.diskon_pic_approval = null;
  }
  return out;
}


/* ============================================================================
 * BARIS PEMBAYARAN (batch N3, brief owner 22 Sep 2026 D4–D7).
 *
 * Owner: "munculkan opsi checkbox di bagian bawah metode pembayaran ...
 * (buatkan dapat ditambahkan lebih dari 1). Saat checkbox-nya diaktifkan, maka
 * muncul 3 kolom yang harus diisi (kolom kosong, nominal, metode pembayaran
 * lagi)". Satu baris = {method, amount, keterangan, metode_id}; baris UTAMA
 * tanpa kolom keterangan (contoh halaman 16), baris TAMBAHAN wajib keterangan.
 *
 * Server menurunkan penanda dokumen `bayar_per_baris` dari `skema_nota` 3 dan
 * menegakkan aturan yang SAMA (`routers/sales.py::terapkan_baris_pembayaran`);
 * fungsi di sini mencerminkannya supaya tombol simpan tidak berujung 400
 * (aturan #3). Kesetaraan dijaga `backend/tests/test_n3_layar_js.py`.
 * ========================================================================== */

export const SKEMA_NOTA_BAYAR = 3;

const kunciMetode = (m) => (m || "").trim().toLowerCase().split(/\s+/).join(" ");
export const adalahKreditReturMetode = (m) => kunciMetode(m) === "kredit retur";
const rupiah = (v) => "Rp " + Math.round(Number(v) || 0).toLocaleString("id-ID");

/** Baris form baru. `_ikutSisa` = nominal baris UTAMA mengikuti sisa tagihan
 *  (nota tunai) sampai kasir mengubahnya sendiri. */
export function barisBaru(method = "Cash", amount = 0, ikutSisa = false) {
  return { method, amount, keterangan: "", metode_id: null, _ikutSisa: ikutSisa };
}

/** Dokumen/draf → baris form. Dokumen BERTANDA: barisnya apa adanya (baris
 *  bernominal 0 sudah dibuang server; nota tanpa baris → satu baris utama 0).
 *  Dokumen LAMA (rancangan N3 K27): split → barisnya; bertempo non-split →
 *  satu baris sebesar uang muka (`harga_terima`); tunai non-split → satu baris
 *  sebesar uang di kasir yang mengikuti sisa. Tanpa konversi ini uang muka
 *  draf lama lenyap saat disimpan ulang dan pelanggan ditagih ulang. */
export function barisPembayaranDariDokumen(dok, { diTempat = 0 } = {}) {
  const x = dok || {};
  const metode = x.metode_pembayaran || "Cash";
  const pays = (x.payments || []).filter(p => (Number(p?.amount) || 0) > 0);
  if (pays.length) {
    return pays.map(p => ({ method: p.method || "", amount: Number(p.amount) || 0,
      keterangan: p.keterangan || "", metode_id: p.metode_id || null, _ikutSisa: false }));
  }
  const tempo = !!(x.tempo_pembayaran || "").trim();
  if (x.bayar_per_baris) return [barisBaru(metode, 0, false)];
  if (tempo) return [barisBaru(metode, Number(x.harga_terima) || 0, false)];
  return [barisBaru(metode, Math.max(0, Number(diTempat) || 0), true)];
}

/** Uang muka yang tersimpan di draf LAMA bertempo (null = bukan draf seperti
 *  itu). Layar menanyakan perubahan angka ini sebelum simpan — server menolak
 *  perubahan yang tidak dinyatakan (`uang_muka_dikonfirmasi`). */
export function uangMukaBentukLama(dok) {
  const x = dok || {};
  if (x.bayar_per_baris || !(x.tempo_pembayaran || "").trim()) return null;
  const pays = (x.payments || []).filter(p => (Number(p?.amount) || 0) > 0);
  if (pays.length) {
    return bulat2(pays.filter(p => !adalahKreditReturMetode(p.method))
      .reduce((a, p) => a + (Number(p.amount) || 0), 0));
  }
  return bulat2(Number(x.harga_terima) || 0);
}

/** Baris UTAMA mengikuti sisa tagihan (hanya nota tunai, hanya selama belum
 *  disentuh). Mengembalikan array YANG SAMA bila tidak ada perubahan supaya
 *  efek layar tidak berputar. */
export function ikutkanSisa(rows, { diTempat = 0, tempo = false } = {}) {
  if (!rows?.length || tempo || !rows[0]._ikutSisa) return rows;
  const lain = rows.slice(1).reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const nilai = bulat2(Math.max(0, (Number(diTempat) || 0) - lain));
  if (Math.abs((Number(rows[0].amount) || 0) - nilai) < 0.005) return rows;
  return [{ ...rows[0], amount: nilai }, ...rows.slice(1)];
}

/** Tempo dinyalakan: baris utama jadi UANG MUKA opsional bernilai 0 — bawaan
 *  "ikut sisa" nota tunai adalah racun untuk nota bertempo (nota tercatat
 *  lunas, piutang tidak lahir; pola PIU-2026-00018). Tempo dimatikan: baris
 *  utama kembali mengikuti sisa — KECUALI dokumen yang TERSIMPAN bertempo
 *  (`dariTempoTersimpan`, edit Input Penjualan, tinjauan 2): di sana tempo
 *  dimatikan berarti MEMBATALKAN piutang dan menyatakan uang sudah diterima,
 *  jadi baris utama 0 dan tidak mengikuti sisa sampai diketik atau "Isi sisa"
 *  ditekan — angka bawaan layar bukan pernyataan pelunasan. */
export function aturBarisUtamaTempo(rows, tempo, { dariTempoTersimpan = false } = {}) {
  const ikut = !tempo && !dariTempoTersimpan;
  if (!rows?.length) return [barisBaru("Cash", 0, ikut)];
  const [u, ...lain] = rows;
  const nol = tempo || dariTempoTersimpan;
  return [{ ...u, amount: nol ? 0 : u.amount, _ikutSisa: ikut }, ...lain];
}

/** Baris form → payload. Baris bernominal 0 ikut dikirim (server membuangnya);
 *  `metode_id` dikirim tetapi server menurunkannya ulang dari master. */
export function barisKePayload(rows) {
  return (rows || []).map(b => ({
    method: (b.method || "").trim(),
    amount: bulat2(Number(b.amount) || 0),
    keterangan: (b.keterangan || "").trim() || null,
    metode_id: b.metode_id || null,
  }));
}

/** Ringkasan blok pembayaran layar dari rincian (`rincianBayar`) + baris —
 *  cermin aturan server (`periksa_baris_pembayaran`, `uang_kasir_nota`). */
export function ringkasBlokPembayaran(r, rows, { tempo = false } = {}) {
  const isi = (rows || []).filter(b => (Number(b.amount) || 0) > 0);
  const jumlah = bulat2(isi.reduce((a, b) => a + (Number(b.amount) || 0), 0));
  const jumlahNonKr = bulat2(isi.filter(b => !adalahKreditReturMetode(b.method))
    .reduce((a, b) => a + (Number(b.amount) || 0), 0));
  const dp = Number(r?.dp) || 0;
  const harus = Number(r?.harusDibayar) || 0;
  const downgrade = harus < -0.5;
  return {
    nilaiTagihan: Number(r?.nilaiTagihan) || 0,
    tradeIn: Number(r?.tradeIn) || 0,
    dp, harus, downgrade,
    kembaliKePelanggan: downgrade ? bulat2(-harus) : 0,
    harusDiKasir: bulat2(Math.max(0, harus - dp)),
    jumlah, jumlahNonKr,
    selisih: tempo || downgrade ? 0 : bulat2(harus - dp - jumlah),
    sisaPiutang: tempo ? bulat2(Math.max(0, harus - dp - jumlah)) : 0,
    uangKasir: uangKasir(r, isi, { tempo }),
  };
}

/** Pesan yang SAMA artinya dengan 400 server untuk blok pembayaran (null = sah). */
export function galatBarisPembayaran(rows, r, { tempo = false } = {}) {
  const semua = rows || [];
  for (let i = 0; i < semua.length; i++) {
    const b = semua[i];
    const amt = Number(b.amount) || 0;
    if (amt <= 0) continue;
    if (!(b.method || "").trim()) return `Baris pembayaran ke-${i + 1} belum memilih metode pembayaran.`;
    if (i >= 1 && !(b.keterangan || "").trim()) {
      return `Pembayaran tambahan ke-${i} (${b.method} ${rupiah(amt)}) wajib diisi kolom keterangannya.`;
    }
  }
  const k = ringkasBlokPembayaran(r, semua, { tempo });
  if (k.downgrade) {
    if (tempo) return "Nota downgrade (trade-in melebihi tagihan) tidak bisa bertempo — pilih tanpa tempo.";
    if (k.jumlah > 0) return "Nota downgrade: pelanggan tidak membayar apa pun di kasir — kosongkan baris pembayaran.";
    return null;
  }
  if (tempo) {
    if (k.jumlah + k.dp > k.harus + TOLERANSI_HARGA_TERIMA) {
      return `Uang muka ${rupiah(k.jumlah)} melebihi yang harus dibayar ${rupiah(k.harus)}.`;
    }
    if (k.jumlahNonKr + k.dp >= k.harus - TOLERANSI_HARGA_TERIMA) {
      return "Nota bertempo tetapi seluruh tagihan dibayar di tempat — pilih 'Tanpa tempo', atau kosongkan uang mukanya.";
    }
    return null;
  }
  if (Math.abs(k.jumlah + k.dp - k.harus) > TOLERANSI_HARGA_TERIMA) {
    return `Pembayaran ${rupiah(k.jumlah)}${k.dp ? ` + DP ${rupiah(k.dp)}` : ""} tidak sama dengan yang harus `
      + `dibayar ${rupiah(k.harus)} — selisih ${rupiah(k.harus - k.dp - k.jumlah)}.`;
  }
  return null;
}

/** Bagian PEMBAYARAN payload layar N3 (dipakai Input Nota & Input Penjualan). */
export function bagianBayarPayload(form) {
  const baris = barisKePayload(form?.payments);
  const isi = baris.filter(b => b.amount > 0);
  return {
    skema_nota: SKEMA_NOTA_BAYAR,
    payments: baris,
    metode_pembayaran: baris[0]?.method || "Cash",
    is_split: isi.length > 1,
    // Form N3 tidak punya Harga Terima; server menolak angka selain 0.
    harga_terima: 0,
  };
}

/* Rekening pengembalian nota DOWNGRADE (tinjauan 1 N3) — cermin
 * `routers/sales.py::rekening_pengembalian` & `wajibkan_rekening_pengembalian`.
 * Server mewajibkannya di SEMUA jalur tulis nota bertanda, dan mengunci
 * rekening yang SUDAH lengkap dari edit nota (penggantian hanya lewat Finance
 * saat membayar, SPV/CEO + alasan). */
export const KUNCI_REKENING = ["refund_nama_bank", "refund_no_rekening", "refund_atas_nama"];
const LABEL_REKENING = {
  refund_nama_bank: "Nama Bank", refund_no_rekening: "Nomor Rekening", refund_atas_nama: "Atas Nama",
};
const rapiSpasi = (v) => String(v ?? "").split(/\s+/).filter(Boolean).join(" ");

export function rekeningPengembalian(dok) {
  return Object.fromEntries(KUNCI_REKENING.map(k => [k, rapiSpasi(dok?.[k])]));
}

export function rekeningLengkap(rek) {
  return KUNCI_REKENING.every(k => !!rek?.[k]);
}

/** Pesan yang SAMA dengan 400 server (null = sah / bukan downgrade). */
export function galatRekeningPengembalian(form, { downgrade = false } = {}) {
  if (!downgrade) return null;
  const r = rekeningPengembalian(form);
  const kurang = KUNCI_REKENING.filter(k => !r[k]).map(k => LABEL_REKENING[k]);
  return kurang.length
    ? `Transaksi downgrade — ${kurang.join(", ")} wajib diisi untuk pengembalian dana`
    : null;
}

/** Bagian rekening payload. Rekening yang sudah terkunci di dokumen TIDAK
 *  dikirim sama sekali (server menolak 409 kalau berbeda; tidak dikirim =
 *  nilai tersimpan dipertahankan). */
export function bagianRekeningPayload(form, { kirim = true } = {}) {
  if (!kirim) return {};
  const r = rekeningPengembalian(form);
  return Object.fromEntries(KUNCI_REKENING.map(k => [k, r[k] || null]));
}

/** Total Pembayaran kasir dari kolom `InputRupiah bolehKosong`: kosong → null
 *  (server: "Isi Total Pembayaran"), 0 yang DIKETIK → 0 (sah untuk nota
 *  bertempo tanpa uang muka / downgrade). */
export function nilaiTotalKasir(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
